import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { isUnauthorized, useSession } from '@/lib/session';
import { ErrorState } from '@/components/ui/error-state';
import { Spinner } from '@/components/ui/spinner';

/**
 * Renders its children only once `GET /api/auth/session` has succeeded.
 *
 * Three outcomes, and keeping them apart is the whole point (CONTRACT §7, UI
 * rule 1):
 *
 *   still resolving -> full-page spinner. NOT the login screen: flashing a
 *                      login form at someone who is signed in, every cold
 *                      start, trains them to retype a password they did not
 *                      need to.
 *   401             -> redirect to /login, remembering where they were headed
 *                      so the deep link survives the round trip.
 *   any other error -> <ErrorState> with a retry. A server that is down is not
 *                      a user who is signed out, and bouncing them to a login
 *                      form they cannot complete hides the real problem.
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const session = useSession();

  if (session.isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas">
        <Spinner size="lg" label="Checking your session" />
      </div>
    );
  }

  if (session.isError) {
    if (isUnauthorized(session.error)) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      );
    }

    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas px-4">
        <ErrorState
          error={session.error}
          onRetry={() => {
            void session.refetch();
          }}
          className="w-full max-w-md"
        />
      </div>
    );
  }

  return <>{children}</>;
}

export default RequireAuth;
